import { Lock, AlertTriangle } from 'lucide-react'
import UnlockReport from '@/components/tools/UnlockReport'

/**
 * Shared teaser-state renderer for the free /tools/* surface. Renders the real
 * score + grade, the single top issue, locked-section placeholders, and the
 * unlock CTA. Receives only the sanctioned teaser payload from
 * `buildTeaserResponse` — no full findings or raw report data in props.
 */

export interface TeaserReport {
  reportId?: string
  url?: string
  score: number
  grade: string
  /** 'withheld' when the scan was too blocked to grade honestly */
  scoreStatus?: 'graded' | 'withheld'
  topIssue: {
    severity: 'low' | 'medium' | 'high'
    title: string
    summary: string
  } | null
  lockedSummary: {
    additionalFindings: number
    sections: string[]
  }
}

interface TeaserResultProps {
  report: TeaserReport
  toolName: string
  unlockPriceUsd?: number
}

const SEVERITY: Record<
  'high' | 'medium' | 'low',
  { label: string; color: string }
> = {
  high: { label: 'High', color: '#F90B8A' },
  medium: { label: 'Medium', color: '#f97316' },
  low: { label: 'Low', color: '#FDC304' },
}

function gradeColor(grade: string): string {
  switch (grade.trim().charAt(0).toUpperCase()) {
    case 'A':
      return '#22c55e'
    case 'B':
      return '#008dcc'
    case 'C':
      return '#eab308'
    case 'D':
      return '#f97316'
    default:
      return '#F90B8A'
  }
}

export default function TeaserResult({
  report,
  toolName,
  unlockPriceUsd = 5,
}: TeaserResultProps) {
  const color = gradeColor(report.grade)
  const { topIssue, lockedSummary } = report

  return (
    <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
      <div className='flex items-center justify-between mb-5'>
        <span className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground leading-none'>
          {toolName} result
        </span>
        {report.url ? (
          <span className='text-[11px] font-medium text-muted-foreground leading-none truncate max-w-[50%]'>
            {report.url}
          </span>
        ) : null}
      </div>

      {/* Real score + grade — the part the free run earns. A withheld scan
          earned nothing gradeable, so it must never render a number. */}
      {report.scoreStatus === 'withheld' ? (
        <div className='flex flex-col items-center gap-3 mb-6 text-center'>
          <AlertTriangle size={40} style={{ color: '#FDC304' }} />
          <p className='text-2xl font-bold text-foreground leading-none'>
            Grade withheld
          </p>
          <p className='text-sm text-muted-foreground max-w-sm leading-relaxed'>
            Your server (CDN/WAF) blocked most of our checks, so there is not
            enough verified data to grade this site. Allowlist our checker or
            relax the bot challenge, then run it again for a real score.
          </p>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-3 mb-6'>
          <p className='text-6xl font-black leading-none' style={{ color }}>
            {report.score}
            <span className='text-2xl text-muted-foreground'>/100</span>
          </p>
          {report.grade ? (
            <span
              className='inline-block text-sm font-bold px-3 py-1'
              style={{ backgroundColor: `${color}15`, color }}
            >
              Grade {report.grade}
            </span>
          ) : null}
        </div>
      )}

      {/* The single real top issue. */}
      {topIssue ? (
        <div className='bg-background border border-line-strong p-4 mb-5'>
          <div className='flex items-center gap-2 mb-2'>
            <AlertTriangle
              size={14}
              className='shrink-0'
              style={{ color: SEVERITY[topIssue.severity].color }}
            />
            <span className='text-sm font-semibold text-foreground leading-none'>
              {topIssue.title}
            </span>
            <span
              className='ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 shrink-0'
              style={{
                backgroundColor: `${SEVERITY[topIssue.severity].color}15`,
                color: SEVERITY[topIssue.severity].color,
              }}
            >
              {SEVERITY[topIssue.severity].label}
            </span>
          </div>
          {topIssue.summary ? (
            <p className='text-sm text-foreground/80 leading-relaxed'>
              {topIssue.summary}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Locked sections — counts/labels only, no real data. */}
      <div className='space-y-2 mb-6'>
        {lockedSummary.additionalFindings > 0 ? (
          <div className='flex items-center gap-3 bg-background border border-line-strong p-3'>
            <Lock size={14} className='text-muted-foreground shrink-0' />
            <span className='text-sm font-medium text-foreground leading-none'>
              {lockedSummary.additionalFindings} more recommendation
              {lockedSummary.additionalFindings === 1 ? '' : 's'}
            </span>
            <span className='ml-auto inline-block h-3 w-24 rounded bg-foreground/15' />
          </div>
        ) : null}
        {lockedSummary.sections.map(section => (
          <div
            key={section}
            className='flex items-center gap-3 bg-background border border-line-strong p-3'
          >
            <Lock size={14} className='text-muted-foreground shrink-0' />
            <span className='text-sm font-medium text-foreground leading-none'>
              {section}
            </span>
            <span className='ml-auto inline-block h-3 w-28 rounded bg-foreground/15' />
          </div>
        ))}
      </div>

      {/* Unlock CTA — opens the Paddle checkout overlay in place. */}
      <div
        className='border border-line-strong p-5 text-center'
        style={{
          background:
            'linear-gradient(135deg, #0d132108, #008dcc05, #008dcc05)',
        }}
      >
        <UnlockReport priceUsd={unlockPriceUsd} reportId={report.reportId} />
        <p className='text-xs text-muted-foreground mt-3'>
          Full findings &middot; Fix prompts &middot; 1 platform credit to run
          your first audit
        </p>
      </div>
    </div>
  )
}
